import React, {useEffect, useRef, useState} from 'react';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import ImageWrapper from '../ImageWrapper/ImageWrapper';
import Color from '../../helpers/Color';
import './Button.css';

function Button({
    icon,
    label,
    labelColor,
    backgroundColor,
    borderColor,
    borderWidth,
    borderRadius,
    padding,
    paddingTarget,
    margin,
    width,
    minWidth,
    maxWidth,
    height,
    minHeight,
    maxHeight,
    transition,
    fontSize,
    boxShadow,
    isLoading,
    loadLabel,
    disabled,
    showPulseEffect,
    notificationNumber,
    notificationBackgroundColor,
    notificationLabelColor,
    onClick,
    children
}) {
    const ref = useRef(null);
    const [pulseEffect, setPulseEffect] = useState(null);

    useEffect(() => {
        if (showPulseEffect) startPulseEffect();
        else stopPulseEffect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [backgroundColor, showPulseEffect]);

    const handleClick = (event) => {
        event.stopPropagation();
        if (onClick && !disabled && !isLoading) onClick();
    };

    const buttonStyle = () => {
        return {
            width: width || 'auto',
            minWidth: minWidth || 'unset',
            maxWidth: maxWidth || 'unset',
            height: height || 'auto',
            minHeight: minHeight || 'unset',
            maxHeight: maxHeight || 'unset',
            borderRadius: borderRadius === undefined || borderRadius === null ? 50 : borderRadius,
            margin: margin || 0,
            fontSize: fontSize || 20
        };
    };

    const buttonBoxShadowStyle = () => {
        let style = {
            borderWidth: borderWidth || 2,
            borderRadius: borderRadius === undefined || borderRadius === null ? 50 : borderRadius,
            boxShadow: boxShadow || '0 0 10px rgba(0, 0, 0, 0.5)',
            padding: paddingTarget === 'container' ? (padding || '15px 30px') : 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            alignContent: 'center'
        };
        if (disabled || isLoading) {
            style.color = '#EEEEEE';
            style.borderColor = '#909090';
            style.background = '#909090';
        } else {
            style.color = labelColor || '#6F909D';
            style.borderColor = borderColor || '#B3E9FF';
            style.background = backgroundColor || '#B3E9FF';
        }
        return style;
    };

    const labelStyle = () => {
        return {
            padding: !children && paddingTarget === 'label' ? (padding || '15px 30px') : 0
        };
    };

    const notificationStyle = () => {
        return {
            background: notificationBackgroundColor || '#FFB819',
            color: notificationLabelColor || '#111111'
        };
    };

    const startPulseEffect = () => {
        if (ref) {
            stopPulseEffect();
            const rgb = Color.hexToRgb(backgroundColor) || Color.hexToRgb(borderColor) || {r: 0, g: 91, b: 128};
            // noinspection JSUnresolvedReference
            const pulseEffect = ref.current.animate([
                {boxShadow: `0 0 0 0 rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.9)`},
                {boxShadow: `0 0 0 10px rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`},
                {boxShadow: `0 0 0 0 rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0)`}
            ], {
                duration: 1500,
                iterations: Infinity
            });
            setPulseEffect(pulseEffect);
        }
    };

    const stopPulseEffect = () => {
        if (pulseEffect) {
            pulseEffect.cancel();
            setPulseEffect(undefined);
        }
    };

    return (
        <div ref={ref} className={`button${transition === 'container' ? ' transition' : ''}`} onClick={handleClick} style={buttonStyle()}>
            <div className='button_box-shadow' style={buttonBoxShadowStyle()}>
                {!!notificationNumber && notificationNumber > 0 && (
                    <div className='button_notification' style={notificationStyle()}>{notificationNumber}</div>
                )}
                {isLoading && (
                    <div className='button_loader'>
                        <SpinnerLoader size={40} color='#ACACAC'/>
                    </div>
                )}
                {!isLoading && icon && (
                    <div className='button_icon-container'>
                        <ImageWrapper
                            image={icon}
                            alt='Filters'
                            display='block'
                            width={30}
                            height={30}
                            errorWidth={undefined}
                            errorHeight={undefined}
                            errorPadding={undefined}
                            margin={5}
                            borderRadius={0}
                            transition={true}
                            loadDelay={0}
                            onLoad={undefined}
                            onMouseEnter={undefined}
                            onMouseLeave={undefined}
                        />
                    </div>
                )}
                <div className={`button_label${transition === 'label' && !disabled ? ' transition' : ''}`} style={labelStyle()}>
                    {children ? children : undefined}
                    {!children ? (isLoading && loadLabel ? loadLabel : label) : undefined}
                </div>
            </div>
        </div>
    );
}

export default Button;